// Общата горна лента на справките и списъците в склада — в десния слот на
// `PageHeader` (на телефон слотът е на цял ред под заглавието). Един и същ ред
// навсякъде, и в Tab реда:
//
//   КАКВО се зарежда (периодът — `PeriodPicker`; в „Наличности" — складът)
//   · „Филтри" (само на телефон: там таблицата е карти и заглавия с фунии няма)
//   · Excel (Ctrl/Cmd+E — `useExcelShortcut`) · Опресни
//
// „Опресни" презарежда заявките на страницата (`refreshOf`); иконата се върти,
// докато някоя от тях тегли, и поне ~0,6 s след клик — бърз отговор иначе не се
// вижда и човек не знае дали е станало. Бутонът НЕ се изключва по време на
// зареждане: изключен бутон губи фокуса и Tab-ът тръгва отначало.
// На телефон Excel и Опресни са само икони (етикетът е в aria-label/title).
import { useEffect, useState, type ReactNode } from "react";
import { FileExcelOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useInventoryShell } from "../shell/InventoryShellContext";
import { FiltersButton } from "../filters/ColumnHeader";
import type { ColumnFilterSection } from "../filters/useColumnFilters";
import { PeriodPicker } from "../period/PeriodPicker";
import type { Period } from "../period/periodPresets";
import { SECONDARY_BUTTON_CLASS } from "./buttonStyles";
import { useExcelShortcut } from "./useExcelShortcut";

// Колко дълго иконата се върти след клик, дори отговорът да е по-бърз
const REFRESH_PULSE_MS = 600;

type Props = {
  // Периодът на справката (липсва в списъците без период)
  period?: Period;
  onPeriodChange?: (period: Period) => void;
  // Собствената контрола на мястото на периода (складът в „Наличности")
  lead?: ReactNode;
  // Секциите на филтрите в колоните — бутонът „Филтри" на телефон
  filterSections?: ColumnFilterSection[];
  onExport: () => void;
  // Няма редове за експорт → бутонът е изключен
  exportDisabled?: boolean;
  // Ctrl/Cmd+E; изключва се, докато друг изглед е „отгоре"
  exportShortcut?: boolean;
  onRefresh: () => void;
  refreshing: boolean;
};

export const ReportToolbar = ({
  period,
  onPeriodChange,
  lead,
  filterSections,
  onExport,
  exportDisabled = false,
  exportShortcut = true,
  onRefresh,
  refreshing,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [pulse, setPulse] = useState(false);

  useExcelShortcut({ onExport, canExport: !exportDisabled, enabled: exportShortcut });

  useEffect(() => {
    if (!pulse) return;
    const timer = setTimeout(() => setPulse(false), REFRESH_PULSE_MS);
    return () => clearTimeout(timer);
  }, [pulse]);

  const refresh = () => {
    setPulse(true);
    onRefresh();
  };
  const spinning = refreshing || pulse;

  const excelLabel = `${t("inventoryPage.exportExcel")} (Ctrl+E)`;
  const refreshLabel = t("inventoryPage.refresh");
  // На телефон — квадратни бутони само с икона, в ред с периода
  const iconButtonClass = isNarrow ? "!h-[38px] !w-[38px] !px-0" : "";

  return (
    <div
      role="group"
      aria-label={t("inventoryPage.tbToolbarAria")}
      className={`flex flex-wrap items-center gap-2 ${isNarrow ? "w-full" : ""}`}
    >
      {period && onPeriodChange && <PeriodPicker value={period} onChange={onPeriodChange} />}
      {lead}
      {isNarrow && filterSections && <FiltersButton sections={filterSections} />}
      {/* Бутоните вдясно; собствената контрола (складът) сама пълни реда */}
      {isNarrow && !lead && <div className="flex-1" />}
      <button
        type="button"
        onClick={onExport}
        disabled={exportDisabled}
        aria-label={excelLabel}
        aria-keyshortcuts="Control+E Meta+E"
        title={excelLabel}
        className={`${SECONDARY_BUTTON_CLASS} ${iconButtonClass}`}
      >
        <FileExcelOutlined className="text-[14px] text-ok-deep" />
        {!isNarrow && t("inventoryPage.scExcel")}
      </button>
      <button
        type="button"
        onClick={refresh}
        aria-label={refreshLabel}
        aria-busy={spinning}
        title={refreshLabel}
        className={`${SECONDARY_BUTTON_CLASS} ${iconButtonClass}`}
      >
        <ReloadOutlined spin={spinning} className="text-[13px] text-body" />
        {!isNarrow && refreshLabel}
      </button>
    </div>
  );
};

export default ReportToolbar;
